import { FormModel } from '@alfresco/adf-core';
import { FileModel } from '@alfresco/adf-content-services';
import { Node } from '@alfresco/js-api';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { NodeInfoService } from '../../services/node.service';
import { SiteConfigService } from '../../services/site-config.service';
import { SetUploadCompletedAction } from '../../store';
import { getCurrentFolder } from '../../store/selectors/app.custom-selectors';
import { AppStore } from '../../store/states/app.state';

@Component({
  selector: 'wk-file-upload-dialog',
  templateUrl: './wk-file-upload.component.html',
  styleUrls: ['./wk-file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class WkFileUploadComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('uploadButton', { read: ElementRef })
  uploadButton: ElementRef;
  fileNames: string[] = [];
  directory: Node;
  files: FileModel[];
  documentType: any;
  currentSite: string;
  protected readonly onDestroy$ = new Subject<void>();
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<WkFileUploadComponent>,
    private readonly siteAppConfig: SiteConfigService,
    private readonly store: Store<AppStore>,
    private readonly nodeService: NodeInfoService
  ) {
    this.files = this.data.files;
    this.fileNames = this.data.fileNamesToUpload;
    this.documentType = this.data.docType;
  }
  ngAfterViewInit(): void {
    fromEvent(this.uploadButton.nativeElement, 'click')
      .pipe(debounceTime(300), takeUntil(this.onDestroy$))
      .subscribe(this.uploadFiles.bind(this));
  }

  ngOnInit(): void {
    this.store.dispatch(new SetUploadCompletedAction(false));
    if (!this.data.directory) {
      this.store
        .select(getCurrentFolder)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((currentFolder) => (this.directory = currentFolder));
    } else {
      this.directory = this.data.directory;
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  closeDialog() {
    this.siteAppConfig.selectedNodeType = [];
    this.dialogRef.close({ uploadQueue: false });
  }

  uploadFiles() {
    this.currentSite = localStorage.getItem('currentSite');
    const checkFilesForDuplicates = this.files.filter(
      (file) => !!file.options.properties.uploadedObject?.duplicate
    );
    if (checkFilesForDuplicates.length) {
      this.nodeService
        .filterDuplicateFiles(checkFilesForDuplicates)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((items) => {
          if (items.length) {
            this.dialogRef.close({
              duplicate: items,
            });
          } else {
            this.proceedToUpload(this.extractFTSPath());
          }
        });
    } else {
      this.proceedToUpload(this.extractFTSPath());
    }
  }

  extractFTSPath(): Array<string> {
    const paths = new Set(
      this.files
        .filter((file) => !!file.options.properties.uploadedObject?.fts_path)
        .map((file) => file.options.properties.uploadedObject.fts_path)
    );
    const pathArray = [...paths];
    if (pathArray.length) {
      return pathArray;
    }
    return null;
  }

  proceedToUpload(paths: Array<string>) {
    this.nodeService
      .getFolderConfiguration(paths)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        if (!paths) {
          this.files.forEach((file) => {
            file.options.parentId =
              config['selectedId'] || config['currentFolder'].id;
            this.setPropertiesForFiles(file);
          });
        } else {
          this.files.forEach((file) => {
            const path: string =
              file.options.properties.uploadedObject?.fts_path;
            if (path) {
              file.options.parentId = config[0][path];
            } else {
              file.options.parentId =
                config[1].selectedId || config[1].currentFolder.id;
            }
            this.setPropertiesForFiles(file);
          });
        }
        let uploadQueue = true;
        if (this.data.fileEvent) {
          this.data.fileEvent.resumeUpload();
          uploadQueue = false;
        }
        this.dialogRef.close({
          uploadQueue,
        });
      });
  }

  setPropertiesForFiles(file: FileModel) {
    const props = Object.create(null);
    const form: FormModel = file.options.properties.form;
    Object.keys(form.values).forEach((key: string) => {
      if (form.values[key]) {
        props[key.split('_').join(':')] = form.values[key];
      }
    });
    file.options.properties = props;
  }

  canUploadFiles() {
    for (const file of this.files) {
      const props = file.options.properties.form;
      if (!props) {
        return true;
      }
      if (!props.isValid) {
        return true;
      }
    }
    return false;
  }
}
